import { lazy } from 'react';

const DEFAULT_RETRIES = 2;
const DEFAULT_RETRY_DELAY = 500;

export class OptionalChunkLoadError extends Error {
    constructor(error) {
        super(error?.message || 'Failed to load optional chunk');
        this.name = 'OptionalChunkLoadError';
        this.originalError = error;
    }
}

export const isOptionalChunkLoadError = (error) => error instanceof OptionalChunkLoadError;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retries a dynamic import with exponential backoff. Rspack drops a failed chunk from its
 * registry, so a new import() issues a fresh request instead of replaying the rejection.
 */
export const loadWithRetry = (
    load,
    { retries = DEFAULT_RETRIES, retryDelay = DEFAULT_RETRY_DELAY } = {},
) => {
    const attempt = (remaining, wait) =>
        load().catch((error) => {
            if (remaining <= 0) throw error;
            return delay(wait).then(() => attempt(remaining - 1, wait * 2));
        });

    return attempt(retries, retryDelay);
};

/**
 * Lazy loader for UI the application can run without. Failures are tagged so
 * OptionalLazyBoundary can drop the feature instead of escalating to the app boundary.
 */
export const lazyOptional = (load, options) =>
    lazy(() =>
        loadWithRetry(load, options).catch((error) => {
            throw new OptionalChunkLoadError(error);
        }),
    );
